import React, { SFC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserModel } from '../../../shared/types/user'
import { Grid, CircularProgress, Card, CardContent, Typography, ListItem, TextField, InputAdornment } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { useCalcHeightToBottomPartialList } from '../../../utils/ui'
import { NoData } from '../../../shared/components/NoData'
import SearchIcon from '@material-ui/icons/Search'
import { setUsersSearch } from '../../../redux/actions/users'

const useStyles = makeStyles((theme) => ({
    list: {
        overflowY: 'auto',
        marginTop: theme.spacing(3),
    },
    listItem: {
        margin: theme.spacing(1, 1, 2, 1),
        display: 'flex',
        '& .Mui-selected': {
            borderBottom: `3px solid ${theme.palette.primary.main}`,
            paddingBottom: theme.spacing(2) - 3,
        },
    },
    listItemButton: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'start',
        padding: theme.spacing(2),
    },
    listContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
    },
    listContent: {
        flex: '1 0 auto',
        textAlign: 'left',
        padding: 0,
        '&:last-child': {
            padding: 0,
        },
    },
    search: {
        paddingLeft: theme.spacing(1),
    },
    progress: {
        textAlign: 'center',
        position: 'relative',
        top: '5px'
    },
    dataInfoWrapper: {
        textAlign: 'center',
        paddingLeft: theme.spacing(1),
    },
    input: {
        backgroundColor: '#f5f5f5', 
        borderRadius: theme.shape.borderRadius, 
        overflow: 'hidden',
        border: '1px solid #ddd',

        '& .MuiFilledInput-root': {
            backgroundColor: '#f5f5f5', 
            borderRadius: theme.shape.borderRadius, 
            paddingBottom: '6px',
            height: '40px',
            boxShadow: 'none',
            '&:before, &:after': {
                display: 'none', 
            },
        },
        '& .MuiFilledInput-input': {
            padding: '17px 12px 10px 0px'
        },
        '& .MuiInputLabel-root': {
            top: '-7px', 
            fontSize: '0.9rem', 
            // color: '#9e9e9e',
        },
        '& .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '8px',
        },
    }
}))

interface UserListProps {
    users: UserModel[]
    selectedUser?: UserModel
    onUserSelected(user: UserModel): void
}
const UserList: SFC<UserListProps> = ({ users, selectedUser, onUserSelected }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const usersExecuted = useSelector((state: any) => state.users.items.executed)

    const search = useSelector((state: any) => state.users.search.value);

    const filteredUsers = users.filter((user) => {
        const searchLower = search.toLowerCase();
    
        const simpleMatches =
            user.firstName.toLowerCase().includes(searchLower) ||
            user.lastName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.phone?.toLowerCase().includes(searchLower);

        const rfidMatches = user.rfids?.some((rfid) => 
            rfid.rfid.toLowerCase().includes(searchLower)
        );

        const carMatches = user.cars?.some((car) => 
            car.carLicensePlate.toLowerCase().includes(searchLower)
        );

        // Return true if either name or RFID matches
        return simpleMatches || rfidMatches || carMatches;
    });

    const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setUsersSearch(e.target.value)); // Update Redux state on input change
    };

    const listHeightFill = useCalcHeightToBottomPartialList('listWrapper')

    return (
        <>
            <Grid container>
                <Grid item xs={10} className={classes.search}>
                <TextField
                        fullWidth
                        variant="filled"
                        placeholder={t('users.search')}
                        value={search}
                        onChange={handleSearchChanged}
                        className={classes.input}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={2} className={classes.progress}>
                    {!usersExecuted && <CircularProgress size={30} />}
                </Grid>
                <Grid id="listWrapper" item xs={12} className={classes.list} ref={listHeightFill}>
                    {usersExecuted && filteredUsers.length === 0 ? (
                        <div className={classes.dataInfoWrapper}>
                            <NoData text={t('users.noUsers')} />
                        </div>
                    ) : (
                        filteredUsers.map((user) => (
                                <Card key={user.id} className={classes.listItem} elevation={1}>
                                    <ListItem
                                        button
                                        selected={user === selectedUser}
                                        onClick={() => onUserSelected(user)}
                                        className={classes.listItemButton}
                                    >
                                        <div className={classes.listContentWrapper}>
                                            <CardContent className={classes.listContent}>
                                                <Typography variant="h6">{`${user.firstName} ${user.lastName}`}</Typography>
                                                <Typography component="div" variant="caption">
                                                    {user.email}
                                                </Typography>
                                            </CardContent>
                                        </div>
                                    </ListItem>
                                </Card>
                            ))
                        )}
                </Grid>
            </Grid>
        </>
    )
}

export default UserList
